import React from 'react';
import type { Dispatch, SetStateAction } from 'react';
import useStyles from '../../theme/useStyles';

// Pairs with usePagination():
//   const page = usePagination(filtered);
//   <Pagination {...page} label="events" />
//
// Spreading the hook's result passes currentPage/totalPages/setCurrentPage etc.,
// so the pager can jump to any page, not just step one at a time.
export interface PaginationProps {
  currentPage?: number;
  totalPages?: number;
  totalItems?: number;
  itemsPerPage?: number;
  setCurrentPage?: Dispatch<SetStateAction<number>>;
  /** Rows actually rendered on this page. Falls back to totalItems. */
  itemsOnPage?: number;
  next?: () => void;
  prev?: () => void;
  isFirst?: boolean;
  isLast?: boolean;
  /** Plural noun for the count, e.g. "events". */
  label?: string;
  isDarkMode?: boolean;
}

/**
 * The page numbers to show: all of them when few, otherwise the first, the last,
 * the current and its neighbours, with gaps marked '…'. Keeps the row short even
 * across dozens of pages.
 */
const pageWindow = (current: number, total: number): Array<number | '…'> => {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);

  const pages: Array<number | '…'> = [1];
  if (current > 4) pages.push('…');
  const start = Math.max(2, current - 1);
  const end = Math.min(total - 1, current + 1);
  for (let i = start; i <= end; i += 1) pages.push(i);
  if (current < total - 3) pages.push('…');
  pages.push(total);
  return pages;
};

const Pagination = ({
  currentPage = 1,
  totalPages = 1,
  totalItems = 0,
  itemsPerPage = 10,
  setCurrentPage,
  itemsOnPage,
  next,
  prev,
  isFirst,
  isLast,
  label = 'records',
  isDarkMode
}: PaginationProps) => {
  const s = useStyles(isDarkMode);
  const t = s.t;
  const shown = itemsOnPage ?? totalItems;

  // "Showing 11–20 of 601" reads better than a bare page count.
  const first = totalItems === 0 ? 0 : (currentPage - 1) * itemsPerPage + 1;
  const last = totalItems === 0 ? 0 : first + shown - 1;

  const stepBtn = (disabled?: boolean) => ({
    ...s.pageBtn,
    ...(disabled ? { opacity: 0.5, cursor: 'not-allowed' } : {})
  });

  const numberBtn = (active: boolean) => ({
    minWidth: '34px', padding: '6px 10px', margin: '0 2px', cursor: 'pointer',
    borderRadius: t.radius.sm, fontSize: t.fontSize.sm, fontWeight: t.fontWeight.bold,
    border: `1px solid ${active ? t.primary : t.border}`,
    backgroundColor: active ? t.primary : t.bgMain,
    color: active ? '#FFF' : t.textMain
  });

  return (
    <div style={s.pagination}>
      <span style={s.pageInfo}>
        {totalItems === 0
          ? `No ${label}`
          : `Showing ${first}–${last} of ${totalItems} ${label}`}
      </span>

      <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '2px' }}>
        <button style={stepBtn(isFirst)} disabled={isFirst} onClick={prev}>Previous</button>

        {/* Numbered pages, when the jump handler is available and there's a choice. */}
        {setCurrentPage && totalPages > 1 && pageWindow(currentPage, totalPages).map((p, i) => (
          p === '…'
            ? <span key={`gap-${i}`} style={{ padding: '0 6px', color: t.textFaint }}>…</span>
            : (
              <button
                key={p}
                type="button"
                aria-current={p === currentPage ? 'page' : undefined}
                style={numberBtn(p === currentPage)}
                onClick={() => setCurrentPage(p)}
              >
                {p}
              </button>
            )
        ))}

        <button style={stepBtn(isLast)} disabled={isLast} onClick={next}>Next</button>
      </div>
    </div>
  );
};

export default Pagination;
